/* eslint-disable jsx-a11y/anchor-is-valid */
/**
@format
@flow
*/

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import { useHistory, useLocation } from 'react-router-dom';
import { InView } from 'react-intersection-observer';
import { ContentContainer, PageTemplate } from '@ziynx/components/layout';
import {
  withAppManager, getGraduationInfo, getInitials, useNotification,
  getTotalFilters, findField, LoadingSpinner,
} from '@ziynx/components/utilities';
import { eventNames } from '@ziynx/analytics';
import ActionTypes from '@ziynx/action-types';
import Card from '@ziynx/components/cards';
import Colors from '@ziynx/theme/colors';
import Headline from '@ziynx/components/headlines';
import { ButtonPillGroup, Button } from '@ziynx/components/buttons';
import type { ManagedProps } from '@ziynx/flow-types/appManager';
import type { DiscoverType as DiscoverFromAPI } from '@ziynx/flow-types/discover';
import DiscoverEmptyState from './subcomponents/EmptyState';
import { VIDEO_TRIGGER_TIME } from '../../constants';

type Props = $ReadOnly<{||}>;

type History = {
  push: (pathName: string, params?: { title?: string, }) => void,
};

type DispatchAction = {
  type: string,
  // For filters
  removeFromApplied?: boolean,
  filters?: {
    fieldId?: string,
    content?: string,
  },
} => void

type DiscoverType = {
  ...DiscoverFromAPI,
  addedFilters?: boolean,
  fetching: boolean,
  success: boolean,
  error: any,
};

const LeftSlotWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 688px;
`;

const RightSlotWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
`;

const HeadlineWrapper = styled.div`
  padding-bottom: 40px;
`;

const FilterHeadlineWrapper = styled.div`
  padding-bottom: 24px;
`;

const CardWrapper = styled.div`
  padding-bottom: 64px;
`;

const FilterWrapper = styled.div`
  padding-bottom: 64px;
`;

const PillWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`;

const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 40px;
`;

const BANNER_MESSAGE = {
  successAdd: 'has been added to your watchlist.',
  successRemove: 'has been removed from your watchlist.',
  error: 'An error occurred. Please try again later.',
  removing: 'Removing member.',
  adding: 'Adding member',
};

const ZIYNX_LOGO = 'https://ziynx-media-assets-dev.s3.amazonaws.com/ziynx_logo/ziynx-logo.png';

const DiscoverScreen = (props: ManagedProps<Props>): React$Node => {
  const { viewport } = props;
  const history: History = useHistory();
  const location = useLocation();

  const dispatch: DispatchAction = useDispatch();
  const discoverService: DiscoverType = useSelector(({ discover }) => discover || {}, shallowEqual);
  const watchlistService = useSelector(
    ({ watchlist }) => ({
      ...watchlist[ActionTypes.watchlist.add.GROUP] || {},
      removeUser: watchlist[ActionTypes.watchlist.remove.GROUP] || {},
    }), shallowEqual,
  );
  const filterService = useSelector(({ filter }) => filter?.appliedFilters ?? {}, shallowEqual);
  const [talentName, setTalentName] = useState('');
  const [isTracked, setTracking] = useState({});
  const [isPlayed, setVideoPlayed] = useState({});
  const [isFilterExpanded, setFilterExpanded] = useState(false);

  const { notificationDispatch } = useNotification();

  const isMobile = viewport === 'mobile' || viewport === 'mobileSmall';
  const isNonMobile = viewport !== 'mobile' && viewport !== 'mobileSmall';
  const isNonDesktop = viewport !== 'desktop';

  const WATCHLIST_TOTAL = discoverService?.watchlist?.total;

  const FILTER_TOTAL = getTotalFilters(Object.values(filterService ?? {})).length;

  /** USED FOR PAGE HEADLINE, STICKY HEADER */
  const RIGHT_ACTION_LINK_ARRAY = [
    {
      children: FILTER_TOTAL ? `Filter (${FILTER_TOTAL})` : 'Filter',
      onClick: () => history.push('/discover/filter'),
    },
    {
      children: WATCHLIST_TOTAL ? `Watchlist (${WATCHLIST_TOTAL > 99 ? `${WATCHLIST_TOTAL}+` : WATCHLIST_TOTAL})` : 'Watchlist',
      onClick: () => history.push('/discover/watchlist'),
    },
  ];

  const resetNotification = (isRemove = false) => {
    if (isRemove) {
      dispatch({
        type: ActionTypes.watchlist.clearRemove,
      });
    } else {
      dispatch({
        type: ActionTypes.watchlist.clearAdd,
      });
    }
  };

  useEffect(() => {
    dispatch({
      type: ActionTypes.discover.get.START,
      filters: filterService,
    });
  }, [filterService]);

  useEffect(() => {
    dispatch({
      type: ActionTypes.analytics.logEvent.START,
      name: eventNames.navigated_discover,
      ...(location?.state?.from && {
        properties: {
          referrer: location?.state?.from,
        },
      }),
    });
  }, []);

  /** REMOVING FROM WATCHLIST */
  useEffect(() => {
    if (watchlistService?.removeUser?.success
      && !watchlistService?.removeUser?.fetching
      && talentName) {
      notificationDispatch({
        type: ActionTypes.notifications.add.START,
        payload: {
          variant: 'success',
          content: `${talentName} ${BANNER_MESSAGE?.successRemove}`,
        },
      });
      resetNotification(true);
    }
    if (watchlistService?.removeUser?.error && !watchlistService?.removeUser?.fetching) {
      notificationDispatch({
        type: ActionTypes.notifications.add.START,
        payload: {
          content: BANNER_MESSAGE.error,
          variant: 'error',
        },
      });
      resetNotification(true);
    }
  }, [
    watchlistService?.removeUser?.success,
    watchlistService?.removeUser?.fetching,
    watchlistService?.removeUser?.error,
    talentName,
  ]);

  /** ADDING TO WATCHLIST */
  useEffect(() => {
    if (watchlistService?.success && !watchlistService?.fetching && talentName) {
      notificationDispatch({
        type: ActionTypes.notifications.add.START,
        payload: {
          content: `${talentName} ${BANNER_MESSAGE?.successAdd}`,
          variant: 'success',
        },
      });
      resetNotification();
    }
    if (watchlistService?.error && !watchlistService?.fetching) {
      notificationDispatch({
        type: ActionTypes.notifications.add.START,
        payload: {
          content: BANNER_MESSAGE.error,
          variant: 'error',
        },
      });
      resetNotification();
    }
  }, [watchlistService?.success,
    watchlistService?.fetching,
    watchlistService?.error,
    talentName]);

  const handleWatchlistAction = (talent, fullName) => {
    if (!talent?.profile?.isWatched) {
      setTalentName(fullName);
      dispatch({
        type: ActionTypes.watchlist.add.START,
        body: {
          userId: talent?.profile?._id,
        },
        userInfo: {
          _id: talent?.profile?._id,
          firstName: talent?.profile?.firstName,
          lastName: talent?.profile?.lastName,
          graduation: talent?.profile?.graduation,
          myShot: talent?.profile?.myShot,
        },
      });
      dispatch({
        type: ActionTypes.analytics.logEvent.START,
        name: eventNames.added_talent_to_watchlist,
        properties: {
          talentId: talent?.profile?._id,
        },
      });
    }
    if (talent?.profile?.isWatched) {
      setTalentName(fullName);
      dispatch({
        type: ActionTypes.watchlist.remove.START,
        body: {
          userId: talent?.profile?._id,
        },
        userInfo: {
          _id: talent?.profile?._id,
        },
      });
      dispatch({
        type: ActionTypes.analytics.logEvent.START,
        name: eventNames.removed_talent_from_watchlist,
        properties: {
          talentId: talent?.profile?._id,
        },
      });
    }
  };

  const renderSection = () => (discoverService?.results ?? []).map((item) => {
    if (!item || !item?.results?.length) return null;

    const channelId = item?._id;
    const path = `/discover/channel/${channelId}`;

    const TALENT_RESULTS = (item?.results ?? []).map((talent) => {
      const fullName = `${talent?.profile?.firstName ?? ''} ${talent?.profile?.lastName ?? ''}`;
      const videoSource = talent?.video?.sources?.mp4_1080_square?.url
        || talent?.video?.sources?.mp4?.url;

      const videoId = talent?._id || talent?.responseId;

      const thumbnail = talent?.video?.sources?.thumbnail_square?.url
        || talent?.video?.sources?.thumbnail?.url;

      const thumbnailOptimized = talent?.video?.sources?.thumbnail_optimized?.url || '';

      return {
        headline: {
          title: {
            text: fullName,
            onClick: () => history.push(`/discover/talent/${talent?.profile?._id}`, {
              name: 'Discover',
              backLink: '/discover',
              from: 'Discover',
            }),
            variant: 'text',
            small: true,
            type: 'heading',
          },
          squircleButton: {
            icon: {
              name: talent?.profile?.isWatched ? 'checkmark-fill' : 'plus',
            },
            inverted: talent?.profile?.isWatched,
            onClick: () => handleWatchlistAction(talent, fullName),
          },
        },
        subtitle: {
          text: '',
        },
        thumbnail: {
          uri: thumbnail || ZIYNX_LOGO,
        },
        thumbnail_optimized: {
          uri: thumbnailOptimized,
        },
        video: {
          uri: videoSource || ' ',
          onPlay: () => {
            dispatch({
              type: ActionTypes.analytics.logEvent.START,
              name: eventNames?.viewed_talent_video,
              properties: {
                videoType: 'statement',
                questionType: item?.name,
                channelId,
                videoId,
              },
            });
            if (!isPlayed[`video_${videoId}`]) {
              setVideoPlayed({
                ...isPlayed,
                [`video_${videoId}`]: false,
              });
            }
          },
          onTimeUpdate: (video) => {
            const { currentTime, duration } = video?.target || {};
            const timeCondition = (
              duration > VIDEO_TRIGGER_TIME) ? VIDEO_TRIGGER_TIME : (duration * 0.1);
              // add in && !discoverService?.videoViewed
            if ((currentTime >= timeCondition) && !discoverService?.viewership?.[videoId]) {
              setVideoPlayed({
                ...isPlayed,
                [`video_${videoId}`]: true,
              });
              /**
               * Eventually have 1 dispatch for tracking below
              */
              dispatch({
                type: ActionTypes.analytics.trackPlay.START,
                id: channelId,
                body: { ids: [videoId] },
              });
              // Sending out notification that MyShot was viewed
              dispatch({
                type: ActionTypes.trackingAction.viewedMyShot.START,
                videoViewed: videoId,
              });
            }
          },
          onEnd: () => {
            if (isPlayed[`video_${videoId}`]) {
              setVideoPlayed({
                ...isPlayed,
                [`video_${videoId}`]: false,
              });
            }
          },
        },
      };
    });

    return (
      <InView
        key={`section_${item?._id}`}
        as="div"
        onChange={(inView) => {
          if (inView && !isTracked[`section_${item?._id}`]) {
            setTracking({
              ...isTracked,
              [`section_${item?._id}`]: true,
            });
            dispatch({
              type: ActionTypes.analytics.trackView.START,
              id: channelId,
              body: {
                ids: (item?.results ?? []).map((i) => i?._id),
              },
            });
          }
        }}
      >
        <CardWrapper key={`videoSquare_${item?._id}`}>
          <Card
            variant="videoSquareGroup"
            headline={{
              title: {
                text: item?.name,
              },
              textLink: {
                children: 'See all',
                onClick: () => history.push(path, { title: item?.name }),
              },
            }}
            data={TALENT_RESULTS}
            viewport={viewport}
            headlineLocation="top"
          />
        </CardWrapper>
      </InView>
    );
  });

  const leftContent = (): React$Node => {
    if (!(discoverService?.results ?? []).length) {
      if (discoverService?.fetching) {
        return (
          <SpinnerWrapper>
            <LoadingSpinner />
          </SpinnerWrapper>
        );
      }

      return (
        <DiscoverEmptyState
          onClick={() => dispatch({ type: ActionTypes.filters.removeAll })}
        />
      );
    }

    return renderSection();
  };

  const leftSlot = (): React$Node => (
    <LeftSlotWrapper>
      <HeadlineWrapper>
        <Headline
          variant="page"
          title={{
            text: 'Discover',
            color: Colors.GrayVeryDark,
          }}
          squircleButton={isNonDesktop && {
            icon: {
              name: 'more',
            },
          }}
          popover={isNonDesktop && {
            links: RIGHT_ACTION_LINK_ARRAY,
          }}
          {...isNonMobile && isNonDesktop && {
            textLink: RIGHT_ACTION_LINK_ARRAY,
          }}
        />
      </HeadlineWrapper>
    </LeftSlotWrapper>
  );

  const rightSlot = (): React$Node => {
    /** WATCHLIST DATA */
    const WATCHLIST_DATA = (discoverService?.watchlist?.data ?? []).map((item) => ({
      label: {
        text: getGraduationInfo(item?.graduation),
        labelPosition: 'bottom',
      },
      text: `${item?.firstName} ${item?.lastName}`,
      color: Colors.GrayDark,
      imageSource: item?.myShot?.sources?.thumbnail_optimized?.url
      || item?.myShot?.sources?.thumbnail?.url,
      squircleText: getInitials(`${item?.firstName} ${item?.lastName}`),
      onClick: () => history.push(`/discover/talent/${item?._id}`, { from: 'Discover' }),
    })).filter((item, index) => index <= 2);

    /** FILTER DATA */
    const FILTER_RESULTS = getTotalFilters(
      Object.values(filterService ?? {}), !isFilterExpanded ? 3 : 0,
    )
      .map((filter: any) => ({
        label: {
          text: filter?.name ? filter?.name : filter,
          color: Colors.GrayVeryDark,
        },
        iconRight: {
          name: 'close',
          color: Colors.GrayVeryDark,
        },
        hoverConfig: {
          iconRight: Colors.PrimaryBlue,
        },
        width: 'auto',
        onClick: () => {
          dispatch({
            type: ActionTypes.filters.remove,
            removeFromApplied: true,
            filters: {
              fieldId: findField(filterService, filter?.name ? filter?.name : filter),
              content: filter?.name ? filter?.name : filter,
            },
          });
        },
      }));

    return (
      <RightSlotWrapper>
        <FilterWrapper>
          <FilterHeadlineWrapper>
            <Headline
              title={{
                text: 'Filter',
                color: Colors.GrayDark,
              }}
              textLink={{
                children: !FILTER_TOTAL ? 'Edit' : `Edit (${FILTER_TOTAL})`,
                onClick: () => { history.push('/discover/filter'); },
              }}
            />
          </FilterHeadlineWrapper>
          <PillWrapper>
            {!FILTER_TOTAL ? (
              <Button
                variant="pill"
                label={{
                  text: 'No filter applied',
                  color: Colors.GrayDark,
                }}
                iconLeft={{
                  name: 'filter',
                  color: Colors.Gray,
                }}
                backgroundColor={Colors.GrayVeryLight}
                onClick={() => { history.push('/discover/filter'); }}
              />
            ) : (
              <>
                <ButtonPillGroup
                  pills={FILTER_RESULTS}
                  stacked={false}
                />
                {FILTER_TOTAL > 3
                && (
                  <Button
                    variant="pill"
                    label={{ text: !isFilterExpanded ? `(${FILTER_TOTAL - 3}) ` : 'See less' }}
                    iconLeft={{
                      name: !isFilterExpanded ? 'plus' : 'minus',
                      color: Colors.GrayVeryDark,
                    }}
                    onClick={() => { setFilterExpanded(!isFilterExpanded); }}
                  />
                )}
              </>
            )}
          </PillWrapper>
        </FilterWrapper>
        <Card
          variant="listItemSquircle"
          headline={{
            title: {
              text: 'Your watchlist',
            },
            ...discoverService?.watchlist?.data?.length && {
              textLink: {
                children: `See all (${WATCHLIST_TOTAL > 99 ? `${WATCHLIST_TOTAL}+` : WATCHLIST_TOTAL})`,
                onClick: () => history.push('/discover/watchlist'),
              },
            },
          }}
          data={WATCHLIST_DATA}
        />

      </RightSlotWrapper>
    );
  };

  const getOffset = () => {
    if (viewport === 'mobileSmall' || viewport === 'mobile') {
      return '84px';
    }
    return '94px';
  };

  const stickyHeaderData = {
    textLinks: RIGHT_ACTION_LINK_ARRAY,
    popover: {
      links: RIGHT_ACTION_LINK_ARRAY,
    },
    title: { text: 'Discover' },
    topOffset: getOffset(),
    squircleButton: {
      icon: {
        name: 'more',
      },
    },
    onClick: () => history.push('/discover/watchlist'), // For SquircleButton functionality
  };

  return (
    <>
      <Helmet>
        <title>Discover | ZIYNX</title>
      </Helmet>
      <ContentContainer offsetTop={isMobile ? 86 : 96}>
        <PageTemplate
          alwaysShowRightSlot
          leftSlot={leftSlot()}
          rightSlot={rightSlot()}
          leftContent={leftContent()}
          stickyHeader={stickyHeaderData}
          rightSlotMarginLeft="40px"

        />

      </ContentContainer>
    </>
  );
};

export default (withAppManager(DiscoverScreen): React$ComponentType<ManagedProps<Props>>);
